import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageInput";
import { Trophy, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import Modal from "@/components/Modal";

const Stat = ({ label, value, accent }) => (
  <div className="bg-white border border-gray-200 rounded p-5" data-testid={`overview-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="label-mono">{label}</div>
    <div className={`stat-number ${accent || ""}`}>{value}</div>
  </div>
);

export default function OverviewTab({ auction, reload }) {
  const [summary, setSummary] = useState(null);
  const [teams, setTeams] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [finalising, setFinalising] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api.get(`/auctions/${auction.id}/reports/summary`),
          api.get(`/auctions/${auction.id}/teams`),
        ]);
        setSummary(s.data);
        setTeams(t.data);
      } catch (e) { /* handled globally */ }
    })();
  }, [auction.id]);

  const finalize = async () => {
    setFinalising(true);
    try {
      await api.post(`/auctions/${auction.id}/finalize`);
      toast.success("Auction finalized & locked");
      setConfirming(false);
      reload?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to finalize");
    } finally {
      setFinalising(false);
    }
  };

  const isCompleted = auction.status === "completed";

  if (!summary) return <div className="label-mono py-16 text-center">Loading overview…</div>;

  return (
    <div className="space-y-8" data-testid="overview-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="label-mono">AUCTION SNAPSHOT</div>
        {!isCompleted && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            data-testid="finalize-lock-button"
            className="btn-ghost !border-red-500/60 !text-red-600 text-xs flex items-center gap-2"
            title="Close this auction now and lock it from further edits"
          >
            <LockKey size={14} weight="fill" /> Finalize & Lock
          </button>
        )}
        {isCompleted && (
          <span
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
            data-testid="finalize-lock-completed"
          >
            <LockKey size={12} weight="fill" /> Completed & Locked
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Stat label="Players" value={summary.total_players} />
        <Stat label="Teams" value={summary.total_teams} />
        <Stat label="Sold" value={summary.sold} accent="text-emerald-600" />
        <Stat label="Unsold" value={summary.unsold} accent="text-amber-600" />
        <Stat label="Available" value={summary.available} accent="text-blue-600" />
        <Stat label="Points Spent" value={summary.total_spent} accent="text-emerald-600" />
      </div>

      {teams.length > 0 && (
        <section>
          <h3 className="font-display uppercase text-3xl mb-4">Teams</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teams.map((t) => {
              const pct = t.budget ? Math.round(((t.budget - t.remaining_budget) / t.budget) * 100) : 0;
              return (
                <div key={t.id} className="bg-white border border-gray-200 rounded p-4" data-testid={`overview-team-${t.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {t.logo_url ? (
                      <img
                        src={resolveImageUrl(t.logo_url)}
                        alt=""
                        className="w-10 h-10 rounded object-cover border flex-shrink-0"
                        style={{ borderColor: `${t.color}55` }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-10 h-10 grid place-items-center rounded flex-shrink-0" style={{ background: `${t.color}22`, border: `1px solid ${t.color}55` }}>
                        <Trophy weight="fill" size={18} style={{ color: t.color }} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-display text-xl uppercase leading-none truncate">{t.name}</div>
                      <div className="label-mono">{t.short_name}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="font-display text-xl">{t.remaining_budget}</div><div className="label-mono">Left</div></div>
                    <div><div className="font-display text-xl">{t.players_bought || 0}</div><div className="label-mono">Bought</div></div>
                    <div><div className="font-display text-xl">{t.budget - t.remaining_budget}</div><div className="label-mono">Spent</div></div>
                  </div>
                  <div className="h-1 bg-gray-100 rounded mt-3 overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: t.color }} /></div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {summary.top_players && summary.top_players.length > 0 && (
        <section>
          <h3 className="font-display uppercase text-3xl mb-4">Top Sales</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {summary.top_players.map((p) => (
              <div key={p.id} className="bg-white border border-gray-200 rounded p-4">
                <div className="label-mono">{p.role}</div>
                <div className="font-display text-lg leading-tight">{p.name}</div>
                <div className="font-display text-2xl text-emerald-600 mt-1">{p.sold_price}<span className="text-xs text-gray-500 ml-1">pts</span></div>
                {p.sold_team && <div className="text-xs text-gray-500 mt-1">→ {p.sold_team.name}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      <Modal
        open={confirming}
        onClose={() => !finalising && setConfirming(false)}
        title="Finalize & Lock Auction?"
        subtitle="This action cannot be undone by admins"
        size="sm"
        testId="finalize-lock-modal"
        footer={
          <>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={finalising}
              className="btn-ghost"
              data-testid="finalize-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={finalize}
              disabled={finalising}
              className="btn-primary !bg-red-600 hover:!bg-red-700 flex items-center gap-2"
              data-testid="finalize-confirm"
            >
              <LockKey size={14} weight="fill" /> {finalising ? "Finalizing…" : "Yes, Finalize & Lock"}
            </button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            Closing the auction marks its status as <b>completed</b> and stops the live floor.
            Admins will no longer be able to add teams, players or change settings.
          </p>
          <p className="text-xs text-gray-500">
            Only the superadmin can reopen a locked auction. Use this when the auction is truly over
            and you want to freeze the final squad list.
          </p>
        </div>
      </Modal>
    </div>
  );
}
